// 规则判定 — applyMove/isLegal/isInCheck/checkmate/stalemate/飞将/和棋
// 不引入外部库, 全部第一性原理.

import {
  Color, GameState, Move, Square, NUM_SQUARES,
  fileOf, rankOf, makeSquare, opposite,
} from './types';
import { generatePseudoMoves, generatePieceMoves } from './moveGen';

// ----- 应用走子 (不可变) -----
export function applyMove(state: GameState, move: Move): GameState {
  const board = state.board.slice();
  const piece = board[move.from];
  if (!piece) throw new Error(`applyMove: no piece at ${move.from}`);
  board[move.from] = null;
  board[move.to] = piece;
  // no-capture/no-pawn reset
  const isCapture = !!move.captured;
  const isPawn = piece.type === 'P';
  const halfmove = isCapture || isPawn ? 0 : state.halfmove + 1;
  const fullmove = state.turn === 'b' ? state.fullmove + 1 : state.fullmove;
  return {
    board,
    turn: opposite(state.turn),
    halfmove,
    fullmove,
  };
}

// ----- 找将 -----
export function findKing(state: GameState, color: Color): Square | null {
  for (let sq = 0; sq < NUM_SQUARES; sq++) {
    const p = state.board[sq];
    if (p && p.color === color && p.type === 'K') return sq;
  }
  return null;
}

// ----- 是否被攻击 (颜色 byColor 的棋子能否吃到 sq) -----
// 用伪合法走子的反向思维太慢, 改用"从 sq 出发反推"算法:
//   - 车/炮/将对每个方向滑动检测
//   - 马检查 8 个 L 形 (注意攻击方向相反时蹩马腿计算)
//   - 兵检查 3 个攻击格
//   - 仕/相只守不攻 (但相可以斜吃, 仕可斜吃) — 实际中国象棋所有棋子按相同规则吃子,
//     所以"攻击格" == "走子格"
// 直接生成 byColor 所有伪合法攻击走法并查 sq 命中
export function isAttacked(state: GameState, sq: Square, byColor: Color): boolean {
  // 临时翻转 turn 让 generatePseudoMoves 生成 byColor 的走法
  const flipped: GameState = { ...state, turn: byColor };
  const moves = generatePseudoMoves(flipped);
  for (const m of moves) {
    if (m.to === sq) return true;
  }
  return false;
}

// ----- 将军 -----
export function isInCheck(state: GameState, color: Color): boolean {
  const king = findKing(state, color);
  if (king === null) return false;
  return isAttacked(state, king, opposite(color));
}

// ----- 飞将 (两王同列且中间无子) -----
export function isFlyingGeneral(state: GameState): boolean {
  const rK = findKing(state, 'r');
  const bK = findKing(state, 'b');
  if (rK === null || bK === null) return false;
  if (fileOf(rK) !== fileOf(bK)) return false;
  const file = fileOf(rK);
  const rR = rankOf(rK), bR = rankOf(bK);
  const [lo, hi] = rR < bR ? [rR, bR] : [bR, rR];
  for (let r = lo + 1; r < hi; r++) {
    if (state.board[makeSquare(file, r)] !== null) return false;
  }
  return true;
}

// ----- 合法走子 (应用后己方不被将军且不飞将) -----
export function isLegal(state: GameState, move: Move): boolean {
  const after = applyMove(state, move);
  // 己方 (走子方) 走完后不能被将军
  if (isInCheck(after, state.turn)) return false;
  // 飞将: 走完后两王不能照面 (视为将军)
  if (isFlyingGeneral(after)) return false;
  return true;
}

export function legalMoves(state: GameState): Move[] {
  return generatePseudoMoves(state).filter(m => isLegal(state, m));
}

// 单子合法走子 (UI 用)
export function legalPieceMoves(state: GameState, from: Square): Move[] {
  const out: Move[] = [];
  // 注意: state.turn 可能与 from 棋子颜色不同 (比如复盘), 这里以 from 棋子颜色为准
  const p = state.board[from];
  if (!p) return out;
  // 临时使 turn == 该子颜色, 生成伪合法走子, 再过滤
  const tmp: GameState = { ...state, turn: p.color };
  const moves = generatePieceMoves(tmp, from);
  for (const m of moves) {
    if (isLegal(tmp, m)) out.push(m);
  }
  return out;
}

// ----- 终局判定 -----
export function isCheckmate(state: GameState): boolean {
  return isInCheck(state, state.turn) && legalMoves(state).length === 0;
}

export function isStalemate(state: GameState): boolean {
  return !isInCheck(state, state.turn) && legalMoves(state).length === 0;
}

// 困毙/将死 → 当前走子方负 (中国象棋规则)
export type GameResult = 'ongoing' | 'red-win' | 'black-win' | 'draw';

export function gameResult(state: GameState): GameResult {
  const redKing = findKing(state, 'r');
  const blackKing = findKing(state, 'b');
  if (redKing === null) return 'black-win';
  if (blackKing === null) return 'red-win';
  if (isCheckmate(state)) {
    // 被将死方 = 当前 turn, 当前 turn 输
    return state.turn === 'r' ? 'black-win' : 'red-win';
  }
  if (isStalemate(state)) {
    // 困毙: 当前 turn 负
    return state.turn === 'r' ? 'black-win' : 'red-win';
  }
  if (state.halfmove >= 120) return 'draw'; // 60 回合无吃子无进兵判和
  if (isInsufficientMaterial(state)) return 'draw';
  return 'ongoing';
}

// 仅剩将 + 仕/士 + 相/象 → 不足判和 (参考 xiangqi.js insufficient_material)
export function isInsufficientMaterial(state: GameState): boolean {
  let hasAttacker = false; // 有无马/车/炮/兵
  for (let sq = 0; sq < NUM_SQUARES; sq++) {
    const p = state.board[sq];
    if (!p) continue;
    if (p.type === 'N' || p.type === 'R' || p.type === 'C' || p.type === 'P') {
      hasAttacker = true;
      break;
    }
  }
  return !hasAttacker;
}
